import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Trophy,
  Users2,
  Inbox,
  Mail,
  Link2,
  Flame,
  Award,
  TrendingUp,
  Sparkles,
  Compass,
  MessageSquare,
  Heart,
  Handshake,
  Briefcase,
  Bell,
  MessagesSquare,
  AtSign,
  Send
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';

const PreviewItem = ({ icon: Icon, label, color }) => (
  <div className="flex items-center gap-2.5 py-1.5">
    <div className={`w-6 h-6 rounded bg-${color}-500/10 flex items-center justify-center shrink-0`}>
      <Icon className={`h-3.5 w-3.5 text-${color}-400`} />
    </div>
    <span className="text-xs text-[#8A93A6]">{label}</span>
  </div>
);

export default function ConnectOverview() {
  return (
    <div className="container mx-auto p-6 max-w-7xl" data-testid="connect-overview-page">
      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-wrap items-center gap-3 mb-2">
          <Link2 className="h-6 w-6 text-blue-400" />
          <h1 className="text-3xl font-bold text-white">Connect</h1>
          <Badge variant="outline" className="text-[10px] border-white/15 text-[#8A93A6] uppercase tracking-wide">
            Relationship Layer
          </Badge>
        </div>
        <p className="text-[#8A93A6]">Your audience, creator relationships, rankings, and engagement — in one place.</p>
      </div>

      {/* Lightweight Positioning */}
      <div
        className="mb-8 rounded-xl border border-white/5 bg-gradient-to-br from-blue-500/[0.04] via-transparent to-purple-500/[0.04] px-5 py-4"
        data-testid="connect-positioning"
      >
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-lg bg-white/[0.04] border border-white/5 flex items-center justify-center shrink-0">
            <Sparkles className="h-4 w-4 text-blue-300" />
          </div>
          <div className="space-y-1">
            <p className="text-sm font-semibold text-white">Relationship + Community Intelligence</p>
            <p className="text-xs text-[#8A93A6] leading-relaxed max-w-2xl">
              Connect brings together leaderboards, creator community, and inbound conversations. One layer to grow rankings, nurture creators, and stay close to your audience.
            </p>
          </div>
        </div>
      </div>

      {/* Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Leaderboards — Elevated */}
        <Link to="/app/connect/leaderboards" className="h-full" data-testid="connect-tile-leaderboards">
          <Card className="glass hover:bg-white/5 transition-all cursor-pointer h-full border-yellow-500/20 ring-1 ring-yellow-500/10 hover:ring-yellow-500/20">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2 text-white">
                <Trophy className="h-5 w-5 text-yellow-400" />
                Leaderboards
                <Badge variant="outline" className="text-[10px] ml-auto border-yellow-500/30 text-yellow-300 uppercase tracking-wider font-semibold">
                  Signature
                </Badge>
              </CardTitle>
              <p className="text-xs text-[#8A93A6] mt-1">Rankings, streaks, and creator momentum across the platform</p>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="border-t border-white/5 pt-3 space-y-0.5">
                <PreviewItem icon={TrendingUp} label="Top Creator Rankings" color="yellow" />
                <PreviewItem icon={Flame} label="Streak Leaders" color="orange" />
                <PreviewItem icon={Award} label="Rewards & Badges" color="amber" />
                <PreviewItem icon={Sparkles} label="Community Performance" color="pink" />
              </div>
            </CardContent>
          </Card>
        </Link>

        {/* Community */}
        <Link to="/app/connect/community" className="h-full" data-testid="connect-tile-community">
          <Card className="glass hover:bg-white/5 transition-all cursor-pointer h-full border-white/5">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2 text-white">
                <Users2 className="h-5 w-5 text-blue-400" />
                Community
              </CardTitle>
              <p className="text-xs text-[#8A93A6] mt-1">Creator ecosystem, discovery, and shared challenges</p>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="border-t border-white/5 pt-3 space-y-0.5">
                <PreviewItem icon={Users2} label="Creator Community" color="blue" />
                <PreviewItem icon={Compass} label="Discovery" color="cyan" />
                <PreviewItem icon={Sparkles} label="Challenges" color="purple" />
                <PreviewItem icon={Heart} label="Community Engagement" color="pink" />
              </div>
            </CardContent>
          </Card>
        </Link>

        {/* Creator Inbox — Internal */}
        <Link to="/app/connect/creator-inbox" className="h-full" data-testid="connect-tile-creator-inbox">
          <Card className="glass hover:bg-white/5 transition-all cursor-pointer h-full border-white/5">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2 text-white">
                <Inbox className="h-5 w-5 text-purple-400" />
                Creator Inbox
                <Badge variant="outline" className="text-[10px] ml-auto border-purple-500/30 text-purple-300 uppercase tracking-wider font-semibold">
                  Internal
                </Badge>
              </CardTitle>
              <p className="text-xs text-[#8A93A6] mt-1">Conversations with creators inside Consistency</p>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="border-t border-white/5 pt-3 space-y-0.5">
                <PreviewItem icon={Handshake} label="Creator-to-Creator Threads" color="purple" />
                <PreviewItem icon={Briefcase} label="Collaboration Requests" color="indigo" />
                <PreviewItem icon={Bell} label="Internal Announcements" color="violet" />
                <PreviewItem icon={MessageSquare} label="Direct Creator Messages" color="pink" />
              </div>
            </CardContent>
          </Card>
        </Link>

        {/* Audience Inbox — External */}
        <Link to="/app/connect/audience-inbox" className="h-full" data-testid="connect-tile-audience-inbox">
          <Card className="glass hover:bg-white/5 transition-all cursor-pointer h-full border-white/5">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2 text-white">
                <Mail className="h-5 w-5 text-green-400" />
                Audience Inbox
                <Badge variant="outline" className="text-[10px] ml-auto border-green-500/30 text-green-300 uppercase tracking-wider font-semibold">
                  External
                </Badge>
              </CardTitle>
              <p className="text-xs text-[#8A93A6] mt-1">DMs, comments, and messages from your audience across platforms</p>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="border-t border-white/5 pt-3 space-y-0.5">
                <PreviewItem icon={Send} label="Direct Messages" color="green" />
                <PreviewItem icon={MessagesSquare} label="Comments & Replies" color="green" />
                <PreviewItem icon={AtSign} label="Mentions & Tags" color="cyan" />
                <PreviewItem icon={Heart} label="Audience Engagement" color="pink" />
              </div>
            </CardContent>
          </Card>
        </Link>
      </div>

      {/* Empty state hint */}
      <div className="mt-10 text-center">
        <p className="text-xs text-[#555E6E]">No conversations yet — connect platforms to begin syncing audience signals</p>
      </div>
    </div>
  );
}
